import React, {useCallback, useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {Badge, Button, Card, Col, Row} from 'react-bootstrap';
import {
    formatDate,
    formatDateWithPattern,
    formatDueDate,
    formatLocalDate,
    getDynamicDisplayValue,
    getRiskLevelStyle
} from '../../../utils/util';
import {
    setManageVisitOpenedFromPageTitle,
    setManageVisitPatient,
    setPatientListType
} from '../../../actions/manageVisitActions';
import {useDispatch, useSelector} from 'react-redux';
import {hidePopulationSearchForm, temporarilyHidePGS} from '../../../actions/populationSearch/populationSearchActions';
import HRMVisitDueIcon from "../../../img/quarterlyvisitdue-icon.svg";
import {AppPropertiesUtil} from "../../../utils/localStorageUtil";
import Tooltip from "react-bootstrap/Tooltip";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import {worklistStatusMessage} from "../../../utils/textValueLists";

const componentName = 'PatientRow';

const isStatusWithDate = (s) =>
  s === "UNABLE_TO_CONTACT" || s === "LEFT_MESSAGE" || s === "CHANGED_PCPS" || s === "PATIENT_REFUSAL";

const getDateSent = (referralDtoList) => {
  const referral = referralDtoList?.[referralDtoList.length - 1] || null;
  return formatLocalDate(referral?.refDateUTC);
};

function WorklistStatusBadge({ patient, idx }) {
  if (!patient.gapStatusChangeDate || !isStatusWithDate(patient.patientGapStatus)) return null;

  return (
    <Badge pill bg="transparent" id={`${componentName}-patientWorklistStatusChangeValue-${idx}`} className="border border-danger text-danger">
      {getDynamicDisplayValue(worklistStatusMessage, patient.patientGapStatus, "value", "text")}{" "}
      {formatDate(patient.gapStatusChangeDate)}
    </Badge>
  );
}

function NextVisitBadge({ idx, text, icon }) {
  const normalizedText = text ? text.trim().toLowerCase() : "";
  if (!normalizedText || normalizedText === "today" || normalizedText.endsWith("ago")) return null;

  return (
    <Badge pill bg="transparent" id={`patientWorklistRecommendedNextVisit-${idx}`} className="border border-secondary text-dark">
      <i className={`fa-regular ${icon} me-1`}></i> {text}
    </Badge>
  );
}

function NoShowStatusBadge({ patient, idx }) {
  const hasNoShowStatus = patient.patientGapStatus === "APPOINTMENT_UNSCHEDULED";
  const isNoShowReason = patient?.appointmentCancelReason === 'NO_SHOW';
  if (!hasNoShowStatus || !isNoShowReason || !patient?.appointmentCancelDate) return null;

  return (
    <Badge pill bg="transparent" id={`${componentName}-patientNoShowBadge-${idx}`} className="border border-danger text-danger">
      No Show on {formatDate(patient.appointmentCancelDate)}
    </Badge>
  );
}

function TOCRow({ id, label }) {
  return (
    <Row>
      <Col>
        <span id={id} className="fw-bold me-2">
          {label}
        </span>
      </Col>
    </Row>
  );
}

function renderTOCSection(patient, idx) {
  const rows = [];

  if (patient.latestAdmitTime) {
    rows.push(
      <TOCRow key="admit" id={`${componentName}-AdmittedOn-${idx}`} label={`Admitted on ${formatDateWithPattern(patient.latestAdmitTime, "MMM dd, yyyy")}`} />
    );
  }
  if (patient.latestDischargeDate) {
    rows.push(
      <TOCRow key="discharge" id={`${componentName}-DischargedOn-${idx}`} label={`Discharged on ${formatDateWithPattern(patient.latestDischargeDate, "MMM dd, yyyy")}`} />
    );
  }
  if (patient.nextVisit) {
    rows.push(
      <TOCRow key="followup" id={`${componentName}-FollowUpDueBy-${idx}`} label={`Follow up due by ${formatDateWithPattern(patient.nextVisit, "MMM dd, yyyy")}`} />
    );
  }

  return rows.length ? <>{rows}</> : null;
}

function renderUserOrScheduleSection({ quickListType, patient, idx }) {
  // Shared blocks
  const postpartumPrefix = "Follow-Up after Child Birth";
  const isPostpartumVisit = (patient.displayName || "").startsWith(postpartumPrefix);

  const DisplayNameBlock = (
    <div id={`${componentName}-patientNextVisitValue-${idx}`} className="fw-bold me-2">
      {isPostpartumVisit ? (
        <OverlayTrigger
          placement="auto"
          overlay={(
            <Tooltip id={`${componentName}-patientPostpartumTooltip-${idx}`}>
              Sensitive context. The data indicates that this patient has recently given birth. The details that follow may be incomplete, and outcomes may include loss, adoption, or other outcomes. As you reach out to assist with follow-up care, please proceed with respect, sensitivity, and empathy.
            </Tooltip>
          )}
        >
          <span>{patient.displayName}</span>
        </OverlayTrigger>
      ) : (
        patient.displayName
      )}
    </div>
  );

  const ChronicConditionBlock = (
      <div id={`${componentName}-patientChronicConditionValue-${idx}`} className="fst-italic me-2">
        {patient.chronicConditionName}
      </div>
  );

  const AppointmentScheduledRow = (
    <Row>
      <Col>
        <span id={`${componentName}-patientAppointmentValue-${idx}`} className="fw-bold me-2">
          {isPostpartumVisit ? (
            <OverlayTrigger
              placement="auto"
              overlay={(
                <Tooltip id={`${componentName}-patientPostpartumTooltip-${idx}`}>
                  Sensitive context. The data indicates that this patient has recently given birth. The details that follow may be incomplete, and outcomes may include loss, adoption, or other outcomes. As you reach out to assist with follow-up care, please proceed with respect, sensitivity, and empathy.
                </Tooltip>
              )}
            >
              <span>{patient.displayName}</span>
            </OverlayTrigger>
          ) : (
            patient.displayName
          )}
          .<br/>Appointment Scheduled for {patient.patientGapAppointmentDate ? formatDateWithPattern(patient.patientGapAppointmentDate, "MMM dd, yyyy") : ""}
        </span>
      </Col>
    </Row>
  );

  const NoShowInline = (
    <Row>
      <Col>
        <div id={`${componentName}-patientNextVisitValue-${idx}`} className="fw-bold">
          {patient.displayName}
        </div>
        <small id={`${componentName}-patientNextVisitValue-${idx}`} className="fw-bold text-danger fst-italic">
          No Show on {formatDate(patient.appointmentCancelDate)}
        </small>
      </Col>
    </Row>
  );

  const isUserWorklist = quickListType === "USER_WORK_LIST" || quickListType === "SCHEDULE";
  const isSingleWorklist = quickListType === "SINGLE_WORK_LIST";

  if (isUserWorklist) {
    if (patient.patientGapStatus === "APPOINTMENT_SCHEDULED" && patient.patientGapAppointmentDate !== null) return AppointmentScheduledRow;

    if (patient.patientGapStatus === "APPOINTMENT_UNSCHEDULED" && patient?.appointmentCancelReason === "NO_SHOW" && patient?.appointmentCancelDate !== null) {
      return NoShowInline;
    }

    return (
      <Row>
        <Col>
          {DisplayNameBlock}
          {patient.gapStatusChangeDate && isStatusWithDate(patient.patientGapStatus) && (
            <small id={`${componentName}-patientWorklistStatusChangeValue-${idx}`} className="fw-bold text-danger fst-italic">
              {getDynamicDisplayValue(worklistStatusMessage, patient.patientGapStatus, "value", "text")}{" "}
              {formatDate(patient.gapStatusChangeDate)}
            </small>
          )}
        </Col>
      </Row>
    );
  }

  if (isSingleWorklist) {
    if (patient.patientGapStatus === "APPOINTMENT_SCHEDULED" && patient.patientGapAppointmentDate !== null) return AppointmentScheduledRow;

    return (
      <div>
        <Row>
          <Col>{DisplayNameBlock}</Col>
        </Row>
        <Row>
          <Col>{ChronicConditionBlock}</Col>
        </Row>
      </div>
    );
  }

  return null;
}

function renderChronicCareManagement(patient, idx) {
  if (!patient.displayName) return null;
  return (
    <div>
      <Row>
        <Col>
          <span id={`${componentName}-VisitDueBy-${idx}`} className="fw-bold me-2">
            {patient.displayName}
          </span>
        </Col>
      </Row>
      <Row>
        <Col>
          <span id={`${componentName}-patientChronicConditionValue-${idx}`} className="fst-italic me-2">
            {patient.chronicConditionName}
          </span>
        </Col>
      </Row>
    </div>
  );
}

function renderCounters(patient, idx) {
  return (
    <div className="d-flex flex-wrap justify-content-start gap-2">
      <span id={`${componentName}-CareOpportunities-${idx}`} className="d-flex align-items-center fw-bold text-nowrap me-3 me-lg-0 me-xl-3">
        <span className="badge bg-light border text-dark p-2 rounded-circle me-2 minb-1">
          {patient.openCareGapsCount ?? "0"}
        </span>{" "}
        Care Opportunities
      </span>
      <span id={`${componentName}-Suspects-${idx}`} className="d-flex align-items-center fw-bold text-nowrap">
        <span className="badge bg-light border text-dark p-2 rounded-circle me-2 minb-1">
          {patient.activeRafsCount ?? "0"}
        </span>{" "}
        Suspect Conditions
      </span>
    </div>
  );
}

function renderMissedOpps(patient, idx, listType) {
  const awaitingChartBlock = (p, i) => ([
    <span key="mo-await" id={`${componentName}-MO-AwaitingChart-${i}`} className="fw-bold me-2">
      {p.displayName ?? ""}
    </span>
  ]);

  const rejectedBlocks = (p, i) => {
    const arr = [];
    if (p.careGapLastRejectionDate) {
      arr.push(
        <span key="mo-rej-care" id={`${componentName}-MO-RejectedCareOpp-${i}`} className="fw-bold me-2">
          Care Opportunity was rejected on {formatDate(p.careGapLastRejectionDate)}
        </span>
      );
    }
    if (p.conditionLastRejectionDate) {
      arr.push(
        <span key="mo-rej-sus" id={`${componentName}-MO-RejectedSuspect-${i}`} className="fw-bold me-2">
          Suspect Condition was rejected on {formatDate(p.conditionLastRejectionDate)}
        </span>
      );
    }
    return arr;
  };

  const visitCompleteBlock = (p, i) => (
    p.completingOrganization
      ? [
          <span key="mo-visit-complete" id={`${componentName}-MO-VisitComplete-${i}`} className="fw-bold fst-italic text-danger me-2">
            Visit completed by {p.completingOrganization}
          </span>
        ]
      : []
  );

  let blocks = [];
  if (listType === "awaitingChart") {
    blocks = awaitingChartBlock(patient, idx);
  } else if (listType === "rejected") {
    blocks = rejectedBlocks(patient, idx);
  }

  // always append visit complete if present
  blocks = blocks.concat(visitCompleteBlock(patient, idx));

  if (!blocks.length) return null;

  return <div className="d-flex flex-wrap justify-content-start gap-2">{blocks}</div>;
}

function renderQuickListContent({ quickListType, patient, idx, text, icon, listType }) {
  if (quickListType === "TRANSITIONS_OF_CARE") {
    return renderTOCSection(patient, idx);
  }

  if (quickListType === "USER_WORK_LIST" || quickListType === "SCHEDULE" || quickListType === "SINGLE_WORK_LIST") {
    return (
      <>
        {renderUserOrScheduleSection({ quickListType, patient, idx })}
        {quickListType === "SINGLE_WORK_LIST" && (
          <div className="d-flex flex-wrap align-items-center gap-2 mt-1">
            <NextVisitBadge patient={patient} idx={idx} text={text} icon={icon} />
            <NoShowStatusBadge patient={patient} idx={idx} />
            <WorklistStatusBadge patient={patient} idx={idx} />
          </div>
        )}
      </>
    );
  }

  if (quickListType === "CHRONIC_CARE_MANAGEMENT") {
    return renderChronicCareManagement(patient, idx);
  }

  if (quickListType === "MISSED_OPPORTUNITIES") {
    return renderMissedOpps(patient, idx, listType);
  }

  return renderCounters(patient, idx);
}

function renderActions(props) {
  const {
    quickListType,
    patient,
    onAction,
    isUnattributed,
    hideResetStatusAction
  } = props;
  const isWorklistType = quickListType === "SINGLE_WORK_LIST";

  if (quickListType === "SCHEDULE") {
    return (
      <div id={`${componentName}-patientActions`} className="btn-group">
        {!isUnattributed && (
          <button id={`${componentName}-patientActionsIcon`} type="button" className="btn btn-link dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
            <i className="fa-solid fa-ellipsis border-0 text-dark"></i>
          </button>
        )}
        <ul id={`${componentName}-patientActionsDropdown`} className="dropdown-menu dropdown-menu-end">
          <li>
            <button id={`${componentName}-patientUnScheduledAppLink`} className="dropdown-item" onClick={() => onAction('unschedule', patient)}>
              Unschedule Appointment
            </button>
          </li>
          <li>
            <button id={`${componentName}-patientRescheduledAppLink`} className="dropdown-item" onClick={() => onAction('reschedule', patient)}>
              Reschedule Appointment
            </button>
          </li>
          {patient.patientGapAppointmentDate && new Date(patient.patientGapAppointmentDate) <= new Date() && (
            <>
              <li><hr className="dropdown-divider" /></li>
              <li>
                <button id={`${componentName}-patientNoShowLink`} className="dropdown-item" onClick={() => onAction('noshow', patient)}>
                  No Show
                </button>
              </li>
            </>
          )}
        </ul>
      </div>
    );
  }

  return (
    <div id={`${componentName}-patientActions`} className="btn-group">
      {!isUnattributed && (
        <button id={`${componentName}-patientActionsIcon`} type="button" className="btn btn-link dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
          <i className="fa-solid fa-ellipsis border-0 text-dark"></i>
        </button>
      )}
      <ul id={`${componentName}-patientActionsDropdown`} className="dropdown-menu dropdown-menu-end">
        <li>
          <button id={`${componentName}-patientScheduledAppLink`} className="dropdown-item" onClick={() => onAction('schedule', patient)}>
            Schedule Appointment
          </button>
        </li>
        <li>
          <button id={`${componentName}-patientChangeStatusLink`} className="dropdown-item" onClick={() => onAction('changestatus', patient)}>
            Change Status
          </button>
        </li>
        {!hideResetStatusAction && isWorklistType && isStatusWithDate(patient?.patientGapStatus) && (
          <li>
            <button id={`${componentName}-patientResetStatusLink`} className="dropdown-item" onClick={() => onAction('resetstatus', patient)}>
              Reset Status
            </button>
          </li>
        )}
      </ul>
    </div>
  );
}

export default function PatientRow({
  patient,
  quickListType,
  idx,
  isPatientSelected,
  handleChangeCheckBox = () => {},
  setSelectedPatient,
  setShowPatientDetails,
  pageTitle,
  onAction,
  isGlobalSearch = false,
  listType,
  activeFilterView,
  hideResetStatusAction = false
}) {
  const dispatch = useDispatch();
  const [isUnattributed, setisUnattributed] = useState(false);
  const state = useSelector(state => state.populationSearch);
  const isShowingPopulationSearchForm = state?.isShowingForm;
  const isPopulationSearchPatientVisible = state?.patientsFound?.isPatientVisible;

  useEffect(() => {
    if (!isPopulationSearchPatientVisible && isShowingPopulationSearchForm) {
      setisUnattributed(true);
    } else {
      setisUnattributed(false);
    }
  }, [isShowingPopulationSearchForm, isPopulationSearchPatientVisible]);

  const isReferralsPage = quickListType === "REFERRALS_BY_USER";

  const { text, icon } = formatDueDate(patient.nextVisitDays);

  const selectionPatient = useMemo(() => {
    if (!patient) return patient;

    const normalize = (value) => {
      if (value === undefined || value === null) return null;
      const trimmed = String(value).trim();
      return trimmed.length ? trimmed : null;
    };

    const baseId =
      normalize(patient.id) ??
      normalize(patient.patientId) ??
      normalize(patient.patientID) ??
      normalize(patient.memberId) ??
      null;

    const visitId =
      normalize(patient.careGapId) ??
      normalize(patient.careGapID) ??
      normalize(patient.patientGapId) ??
      normalize(patient.patientGapID) ??
      normalize(patient.visitGapId) ??
      normalize(patient.visitGapID) ??
      normalize(patient.visitId) ??
      normalize(patient.visitID) ??
      normalize(patient.gapId) ??
      normalize(patient.gapID) ??
      normalize(patient.worklistItemId) ??
      normalize(patient.worklistItemID) ??
      null;

    const scopedKeyParts = [];
    if (baseId) scopedKeyParts.push(baseId);
    if (visitId) scopedKeyParts.push(visitId);
    if (listType) scopedKeyParts.push(`list:${listType}`);
    scopedKeyParts.push(`row:${idx}`);

    const selectionInstanceKey = scopedKeyParts.join("|");

    if (patient.selectionInstanceKey === selectionInstanceKey) {
      return patient;
    }

    return {
      ...patient,
      selectionInstanceKey,
    };
  }, [patient, idx, listType]);

  const handlePatientSelectionChange = useCallback(
    (checked) => {
      handleChangeCheckBox(selectionPatient, checked);
    },
    [handleChangeCheckBox, selectionPatient]
  );

  const patientIsSelected = useMemo(
    () => isPatientSelected(selectionPatient),
    [isPatientSelected, selectionPatient]
  );

  return (
    <Card key={patient.id} id={`${componentName}-patientsCard-${idx}`} className="ps-2 pe-3 mb-3 patientCard">
      <Card.Body>
        <Row>
          <Col xl={4} lg={12} md={12} className="align-self-center">
            <div className="d-flex flex-row align-items-center mb-2 mb-xl-0">
              {!isGlobalSearch && (
                <div className="form-check">
                  <input
                    id={`${componentName}-patientSelect-${idx}`}
                    className="form-check-input me-3"
                    type="checkbox"
                    name={patient.id}
                    checked={patientIsSelected}
                    onChange={(e) => handlePatientSelectionChange(e.target.checked)}
                  />
                  <label htmlFor="basic_checkbox_2" className="visually-hidden">Select patient</label>
                </div>
              )}

              <div>
                <Row>
                  <Col className="d-flex flex-wrap align-items-center">
                    <Button id={`${componentName}-patientFullName-${idx}`} variant="link" type="button" data-bs-toggle={isUnattributed ? undefined : "offcanvas"} data-bs-target={isUnattributed ? undefined : "#patientDetailsModal"}
                      aria-controls="patientDetailsModal" className="fw-bold patientName align-middle me-3 p-0 text-start" disabled={isUnattributed} tabIndex={0}
                      onClick={() => {
                        if (isUnattributed) return;
                        setSelectedPatient(patient);
                        setShowPatientDetails(true);
                        dispatch(setManageVisitPatient(patient));
                        dispatch(hidePopulationSearchForm());
                        dispatch(setManageVisitOpenedFromPageTitle(pageTitle));
                        dispatch(temporarilyHidePGS(true));
                        dispatch(setPatientListType(listType));
                      }}
                      onKeyDown={(e) => {
                        if (isUnattributed) return;
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setSelectedPatient(patient);
                          setShowPatientDetails(true);
                          dispatch(setManageVisitPatient(patient));
                          dispatch(hidePopulationSearchForm());
                          dispatch(setManageVisitOpenedFromPageTitle(pageTitle));
                          dispatch(temporarilyHidePGS(true));
                          dispatch(setPatientListType(listType));
                        }
                      }}
                    >
                      <span style={{ color: isUnattributed ? 'gray' : '' }} className="forced-capital fs-5 d-inline-block">
                        {patient.fullName || patient.surnameCommaName}
                      </span>
                    </Button>

                    <Badge pill bg="light" text="dark" className="border me-1 forced-capital" id={`${componentName}-patientGender-${idx}`}>
                      {patient.gender}
                    </Badge>
                    <Badge pill bg={getRiskLevelStyle(patient.riskLevel)} id={`${componentName}-patientRiskLevel-${idx}`}>
                      {patient.riskLevel}
                    </Badge>
                  </Col>
                </Row>

                <Row>
                  <Col>
                    <div id={`${componentName}-patientMemberID-${idx}`} className="d-inline-block me-3">
                      <span id={`${componentName}-patientMemberIDLabel-${idx}`} className="fw-bold me-2">ID:</span>
                      <span id={`${componentName}-patientMemberIDValue-${idx}`}>{patient.memberId}</span>
                    </div>
                    <div id={`${componentName}-patientDOB-${idx}`} className="d-inline-block">
                      <span id={`${componentName}-patientDOBLabel-${idx}`} className="fw-bold me-2">DOB:</span>
                      <span id={`${componentName}-patientDOBValue-${idx}`}>
                        <span id={`${componentName}-patientDOBDate-${idx}`}>{formatDate(patient.dateOfBirth)}</span>{" "}
                        <Badge id={`${componentName}-patientDOBAge-${idx}`} pill bg="light" text="dark" className="border p-1">{patient.age}</Badge>
                      </span>
                    </div>
                  </Col>
                </Row>

                {isGlobalSearch && (
                  <Row>
                    <Col>
                      <Badge pill bg={patient.active ? "success" : "secondary"} id={`${componentName}-patientActive-${idx}`}>
                        {patient.active ? "Active" : "Inactive"}
                      </Badge>
                    </Col>
                  </Row>
                )}
              </div>
            </div>
          </Col>

          {/* Middle: payor / PCP or program/referral fields or dynamic worklist content */}
          <Col xl={3} lg={4} md={6} className="align-self-center mb-2 mb-md-0">
            {!isReferralsPage && (
              <div id={`${componentName}-patientPayor-${idx}`}>
                <span id={`${componentName}-patientPayorLabel-${idx}`} className="fw-bold me-2">Payor:</span>
                <span id={`${componentName}-patientPayorValue-${idx}`}>{patient.payer}</span>
              </div>
            )}
            <div id={`${componentName}-patientPCPPractice-${idx}`}>
              <span id={`${componentName}-patientPCPPracticeLabel-${idx}`} className="fw-bold me-2">PCP Practice:</span>
              <span id={`${componentName}-patientPCPPracticeValue-${idx}`}>{patient.originatingOrganization}</span>
            </div>
          </Col>

          {isReferralsPage ? (
            <>
              <Col className="align-self-center mb-2 mb-md-0">
                <div id={`${componentName}-patientProgramStatus-${idx}`}>
                  <span id={`${componentName}-patientProgramStatusLabel-${idx}`} className="fw-bold me-2">Program Status:</span>
                  <span id={`${componentName}-patientProgramStatusValue-${idx}`}>{patient.programStatus}</span>
                </div>
              </Col>
              <Col className="align-self-center mb-2 mb-md-0">
                <div id={`${componentName}-patientDateSent-${idx}`}>
                  <span id={`${componentName}-patientDateSentLabel-${idx}`} className="fw-bold me-2">Date Sent:</span>
                  <span id={`${componentName}-patientDateSentValue-${idx}`}>{getDateSent(patient.referralDtoList)}</span>
                </div>
              </Col>
            </>
          ) : (
            <Col xl={3} lg={4} md={6} className="d-flex flex-nowrap justify-content-start justify-content-md-end justify-content-lg-center align-items-center ps-md-5">
              {AppPropertiesUtil.get("react.properties.ecip.eligible.enabled") && patient.ecipEligible && (
                <OverlayTrigger placement="auto" overlay={<Tooltip>{AppPropertiesUtil.get("ecip.visit.monthly") ? "Chronic Care Management Eligible" : "Quarterly Visit Eligible"}</Tooltip>}>
                  <div className="align-ecip" id={`${componentName}-incentive-icon`}>
                    <img src={HRMVisitDueIcon} alt="Incentive Icon" width="25px" className="me-2" />
                  </div>
                </OverlayTrigger>
              )}

              <div className="text-start w-100">
                {renderQuickListContent({ quickListType, patient, idx, text, icon, listType })}
              </div>
            </Col>
          )}

          {/* Right: phone + actions (skip on referrals page) */}
          {!isReferralsPage && (
            <Col xl={2} lg={4} md={12} className="d-flex justify-content-between text-nowrap align-items-center align-self-center">
              <div id={`${componentName}-patientPrimaryPhoneValue-${idx}`} className="h5 mb-0 me-3">
                {patient.primaryPhone}
              </div>

              {patient.active && activeFilterView !== "PRIORITY_ACTIVITY" ? (
                renderActions({
                  quickListType,
                  patient,
                  idx,
                  setSelectedPatient,
                  onAction,
                  isUnattributed,
                  hideResetStatusAction
                })
              ) : (
                <div id={`${componentName}-noActions`} className="btn-group"></div>
              )}
            </Col>
          )}
        </Row>
      </Card.Body>
    </Card>
  );
}

PatientRow.propTypes = {
  patient: PropTypes.object.isRequired,
  quickListType: PropTypes.string,
  idx: PropTypes.number,
  isPatientSelected: PropTypes.func,
  handleChangeCheckBox: PropTypes.func,
  setSelectedPatient: PropTypes.func,
  setShowPatientDetails: PropTypes.func,
  pageTitle: PropTypes.string,
  isGlobalSearch: PropTypes.bool,
  listType: PropTypes.string,
  onAction: PropTypes.func,
  activeFilterView: PropTypes.string,
  hideResetStatusAction: PropTypes.bool
};

TOCRow.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string
};

NextVisitBadge.propTypes = {
  idx: PropTypes.number,
  text: PropTypes.string,
  icon: PropTypes.string
};

WorklistStatusBadge.propTypes = {
  patient: PropTypes.object.isRequired,
  idx: PropTypes.number
};

NoShowStatusBadge.propTypes = {
  patient: PropTypes.object.isRequired,
  idx: PropTypes.number
};
